(() => {
  "use strict";

  /**************
   * Utilities
   **************/
  function tokenFromValue(v) {
    return (
      "-" +
      String(v ?? "")
        .trim()
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    );
  }

  function getRoot(el) {
    return el?.closest?.(".shopify-section") || document;
  }

  function getSelectedColorToken(root) {
    const radio = root.querySelector(
      'input[type="radio"]:checked[name*="colour"], input[type="radio"]:checked[name*="color"]'
    );
    if (radio?.value) return tokenFromValue(radio.value);

    const select = root.querySelector('select[name*="colour"], select[name*="color"]');
    if (select?.value) return tokenFromValue(select.value);

    return "";
  }

  function getAllColorTokens(root) {
    const vals = new Set();

    root
      .querySelectorAll(
        'input[type="radio"][name*="colour"], input[type="radio"][name*="color"]'
      )
      .forEach((r) => vals.add(String(r.value || "").trim()));

    root
      .querySelectorAll('select[name*="colour"], select[name*="color"] option')
      .forEach((o) => vals.add(String(o.value || "").trim()));

    return Array.from(vals)
      .filter(Boolean)
      .map((v) => tokenFromValue(v));
  }

  // Robust match:
  // - allows -grey, -grey-2, -grey2, -grey(1), -grey_main
  // - prevents collisions: "-pink" not matching "-hot-pink"
  function matchesToken(filename, selectedToken, allColorTokens) {
    if (!filename || !selectedToken) return false;

    const lower = String(filename).toLowerCase();
    const sel = String(selectedToken).toLowerCase();

    const esc = sel.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    const re = new RegExp(`${esc}(?:-|\\.|_|\\d|\\(|$)`, "i");
    if (!re.test(lower)) return false;

    if (Array.isArray(allColorTokens) && allColorTokens.length) {
      for (const t of allColorTokens) {
        const tt = String(t || "").toLowerCase();
        if (tt && tt !== sel && tt.endsWith(sel)) {
          if (lower.includes(tt)) return false;
        }
      }
    }

    return true;
  }

  /**************
   * DOM getters
   **************/
  function slides(root) {
    return Array.from(root.querySelectorAll(".product-gallery-item[data-media-name]"));
  }

  function thumbs(root) {
    return Array.from(
      root.querySelectorAll(".product-gallery__thumbnails-holder button.thumbnail[data-index]")
    );
  }

  function findScroller(root) {
    const direct =
      root.querySelector(".css-slider-holder") ||
      root.querySelector(".css-slider") ||
      root.querySelector(".product-gallery");

    if (direct) return direct;

    // Fallback: find first horizontally-scrollable ancestor of a slide
    const firstSlide = slides(root)[0];
    let el = firstSlide?.parentElement;
    for (let i = 0; i < 10 && el; i++) {
      const cs = getComputedStyle(el);
      const ox = cs.overflowX;
      if ((ox === "auto" || ox === "scroll") && el.scrollWidth > el.clientWidth + 2) return el;
      el = el.parentElement;
    }
    return null;
  }

  /**************
   * Hiding that does NOT break Borders mapping
   * - Do NOT remove nodes / do NOT display:none slides
   * - Make hidden slides zero-width + snap:none
   **************/
  function setSlideHidden(slide, hidden) {
    if (!slide) return;

    if (!hidden) {
      slide.classList.remove("is-variant-hidden");
      slide.style.width = "";
      slide.style.flex = "";
      slide.style.margin = "";
      slide.style.padding = "";
      slide.style.overflow = "";
      slide.style.pointerEvents = "";
      slide.style.scrollSnapAlign = ""; // will be forced by CSS to start
      slide.style.visibility = "";
      slide.style.height = "";
      slide.removeAttribute("aria-hidden");
      return;
    }

    slide.classList.add("is-variant-hidden");
    slide.style.width = "0px";
    slide.style.flex = "0 0 0px";
    slide.style.margin = "0";
    slide.style.padding = "0";
    slide.style.overflow = "hidden";
    slide.style.pointerEvents = "none";
    slide.style.scrollSnapAlign = "none";
    slide.style.visibility = "hidden";
    slide.style.height = "0px";
    slide.setAttribute("aria-hidden", "true");
  }

  function setThumbHidden(btn, hidden) {
    if (!btn) return;
    btn.classList.toggle("is-variant-hidden", !!hidden);
    btn.style.display = hidden ? "none" : "";
    btn.style.pointerEvents = hidden ? "none" : "";
  }

  function scrollToSlide(scroller, slideEl) {
    if (!scroller || !slideEl) return;

    const scRect = scroller.getBoundingClientRect();
    const slRect = slideEl.getBoundingClientRect();
    const targetLeft = scroller.scrollLeft + (slRect.left - scRect.left);

    // Temporarily disable snap during programmatic scroll (prevents “jump”)
    scroller.classList.add("variant-filter--nosnap");
    scroller.scrollTo({ left: targetLeft, behavior: "smooth" });
    setTimeout(() => scroller.classList.remove("variant-filter--nosnap"), 260);
  }

  function injectCSSOnce() {
    if (document.getElementById("variant-filter-style")) return;
    const style = document.createElement("style");
    style.id = "variant-filter-style";
    style.textContent = `
      /* Keep snapping consistent across variants/templates */
      .css-slider-holder, .css-slider, .product-gallery {
        scroll-snap-type: x mandatory !important;
      }
      .product-gallery-item {
        scroll-snap-align: start !important;
      }

      /* We still disable snap briefly only during JS scroll */
      .variant-filter--nosnap {
        scroll-snap-type: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**************
   * Core filter
   **************/
  const LAST = new WeakMap(); // root -> last token

  function isFilteringActive(root) {
    // If any thumb/slide is hidden, filtering is active
    return (
      !!root.querySelector(".product-gallery-item.is-variant-hidden") ||
      !!root.querySelector(".product-gallery__thumbnails-holder .thumbnail.is-variant-hidden")
    );
  }

  function applyFilter(root) {
    const token = getSelectedColorToken(root);
    if (!token) return;

    const scroller = findScroller(root);
    const s = slides(root);
    const t = thumbs(root);
    if (!s.length || !t.length) return;

    const allColorTokens = getAllColorTokens(root);

    // Which slide indices are allowed?
    const allowed = [];
    for (let i = 0; i < s.length; i++) {
      const name = s[i].dataset.mediaName || "";
      if (matchesToken(name, token, allColorTokens)) allowed.push(i);
    }

    // If nothing matches, show everything
    const hasMatches = allowed.length > 0;

    for (let i = 0; i < s.length; i++) {
      setSlideHidden(s[i], hasMatches ? !allowed.includes(i) : false);
    }

    for (let i = 0; i < t.length; i++) {
      const idx = Number(t[i].dataset.index);
      setThumbHidden(t[i], hasMatches ? !allowed.includes(idx) : false);
    }

    // Only auto-scroll when colour actually changes
    const last = LAST.get(root) || "";
    if (token !== last) {
      LAST.set(root, token);

      const firstIdx = hasMatches ? allowed[0] : 0;
      const firstSlide = s[firstIdx];
      if (scroller && firstSlide) {
        requestAnimationFrame(() => scrollToSlide(scroller, firstSlide));
      }

      // Active thumb
      const btn = t.find((b) => Number(b.dataset.index) === firstIdx);
      if (btn) {
        t.forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
      }
    }
  }

  /**************
   * FIX 1: Thumbnail clicks (only when filtering is active)
   * We scroll ourselves to the slide that matches the thumb’s data-index.
   * This avoids Borders getting confused by hidden zero-width slides.
   **************/
  function bindThumbClickFix() {
    document.addEventListener(
      "click",
      (e) => {
        const btn = e.target.closest(
          ".product-gallery__thumbnails-holder button.thumbnail[data-index]"
        );
        if (!btn) return;

        const root = getRoot(btn);

        // If no filtering is active, let theme handle normally
        if (!isFilteringActive(root)) return;

        const scroller = findScroller(root);
        const s = slides(root);
        if (!scroller || !s.length) return;

        const idx = Number(btn.dataset.index);
        let target = s[idx];

        // If target is hidden (shouldn’t happen for visible thumbs), find the nearest visible slide
        if (!target || target.classList.contains("is-variant-hidden")) {
          target = s.find((x) => x && !x.classList.contains("is-variant-hidden")) || null;
        }
        if (!target) return;

        // Take over this click so Borders doesn’t try to jump to a hidden/zero-width slide
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Update active thumb + scroll
        thumbs(root).forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");

        scrollToSlide(scroller, target);
      },
      true
    );
  }

  /**************
   * Boot
   **************/
  function boot() {
    injectCSSOnce();
    bindThumbClickFix();

    document.addEventListener(
      "change",
      (e) => {
        const el = e.target;
        if (!el) return;

        const n = (el.name || "").toLowerCase();
        const isColour =
          (el.matches('input[type="radio"]') || el.matches("select")) &&
          (n.includes("colour") || n.includes("color"));

        if (!isColour) return;

        applyFilter(getRoot(el));
      },
      true
    );

    // Apply on load for any product section present
    document.querySelectorAll(".shopify-section").forEach((section) => {
      if (section.querySelector(".product-gallery-item[data-media-name]")) {
        applyFilter(section);
      }
    });
    applyFilter(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
